import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { MenuOptionEditBoxProps } from "components/menu/ui/MenuSectionEditBox";
import { Box, Stack, Tooltip } from "@mui/material";
import MenuItemEditBox from "components/menu/ui/MenuItemEditBox";
import styled from "styled-components";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import CircleIconButton from "components/common/ui/CircleIconButton";
import PillButton from "components/common/ui/PillButton";
import {
  defaultMenuSectionDisplay,
  DisplayMenuItem,
} from "models/menu/menuContentsReducer";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import {
  combineUUIDs,
  getLastUUIDFromPath,
  getMenuOptionByPath,
  removeLastUUIDFromPath,
} from "utils/menu/menu/menuContentDisplay";
import { isDisplayMenuItem } from "models/menu/menu";
import MenuOptionGroupEditBox from "components/menu/ui/MenuOptionGroupEditBox";
import useMenuOptionActions from "hooks/menu/useMenuOptionActions";
import MenuItemTagOverrideButton from "components/menu/ui/MenuItemTagOverrideButton";
import { MenuItemFoodTagOverride } from "client/jspPlatformExperiment";
import { findMolleTag } from "models/menu/menuItemTagOverride";
import { buildFlatFoodLookup } from "utils/food/foodVariantUtils";
import { useAppSelector } from "hooks/useReduxStore";
import currentSiteSelector from "store/selectors/userGroup/currentSiteSelector";

const BorderBox = styled(Box)`
  border: 0.1rem solid #eee;
  padding: 0.5rem;
  border-radius: 0.5rem;
`;

const MainItemTitle = styled(Typography)`
  padding: 0.5rem;
  align-self: center;
  font-weight: 100;
  font-size: 1.5rem;
  color: #a0aab4;
`;

const MenuItemMainEditBox: FC<MenuOptionEditBoxProps> = ({
  foodItemOptions,
  displayMenuOptionUUIDPath,
  menuContents,
  dispatchMenuContents,
}) => {
  const [parentMenuOptionGroup, setParentMenuOptionGroup] = useState(
    defaultMenuSectionDisplay(),
  );
  const { handleMove, handleRemove, handleAdd, handleUpdate } =
    useMenuOptionActions(
      menuContents,
      dispatchMenuContents,
      displayMenuOptionUUIDPath,
    );
  const thisMenuItem = getMenuOptionByPath(
    menuContents,
    displayMenuOptionUUIDPath,
  ) as DisplayMenuItem;

  // The Molle-only preset only applies when the row's food carries the Molle
  // texture tag; read it from the already-loaded food options, no request.
  const foodById = useMemo(
    () => buildFlatFoodLookup(foodItemOptions),
    [foodItemOptions],
  );
  const molleTag = useMemo(
    () => findMolleTag(foodById.get(thisMenuItem?.food_id ?? 0)?.food_tags),
    [foodById, thisMenuItem?.food_id],
  );

  const previewSiteId = useAppSelector(currentSiteSelector.currentSiteId);

  // UPDATE_OPTION merges the partial into the item, so only the overrides
  // list is replaced.
  const handleTagOverridesChange = useCallback(
    (foodTagOverrides: MenuItemFoodTagOverride[]) => {
      handleUpdate({ food_tag_overrides: foodTagOverrides });
    },
    [handleUpdate],
  );

  useEffect(() => {
    const potentialParent = getMenuOptionByPath(
      menuContents,
      removeLastUUIDFromPath(displayMenuOptionUUIDPath) ?? "",
    );
    if (potentialParent && !isDisplayMenuItem(potentialParent)) {
      setParentMenuOptionGroup(potentialParent);
    }
  }, [displayMenuOptionUUIDPath, menuContents]);

  return (
    <BorderBox>
      <Box display="flex" paddingBottom={1}>
        <Box display="flex" padding={1}>
          <Box>
            <Tooltip title="Move this item and it's sides up">
              <SoftSquareIconButton onClick={handleMove("up")}>
                <KeyboardArrowUpIcon />
              </SoftSquareIconButton>
            </Tooltip>
          </Box>
          <Box paddingLeft={1}>
            <Tooltip title="Move this item and it's sides down">
              <SoftSquareIconButton onClick={handleMove("down")}>
                <KeyboardArrowDownIcon />
              </SoftSquareIconButton>
            </Tooltip>
          </Box>
        </Box>
        <MainItemTitle>
          {(parentMenuOptionGroup.name ?? "") !== ""
            ? parentMenuOptionGroup.name
            : `Section ${
                Object.keys(menuContents).findIndex(
                  (key) => key === displayMenuOptionUUIDPath,
                ) + 1
              } - Item`}
          {` ${
            Object.keys(parentMenuOptionGroup.options).findIndex(
              (key) => key === getLastUUIDFromPath(displayMenuOptionUUIDPath),
            ) + 1
          }`}
        </MainItemTitle>
        <Box padding={1}>
          <Tooltip title="Remove this item and it's sides">
            <CircleIconButton positive danger onClick={handleRemove}>
              <DeleteIcon />
            </CircleIconButton>
          </Tooltip>
        </Box>
        <Box padding={1} marginLeft="auto">
          <MenuItemTagOverrideButton
            foodId={thisMenuItem?.food_id ?? 0}
            molleTag={molleTag ?? null}
            previewSiteId={previewSiteId}
            overrides={thisMenuItem?.food_tag_overrides}
            onChange={handleTagOverridesChange}
          />
        </Box>
      </Box>
      <Stack spacing={1}>
        <MenuItemEditBox
          displayMenuOptionUUIDPath={displayMenuOptionUUIDPath}
          menuContents={menuContents}
          dispatchMenuContents={dispatchMenuContents}
          foodItemOptions={foodItemOptions}
        />
        {Object.keys(thisMenuItem.side_option_groups).map((itemSideUUID) => (
          <MenuOptionGroupEditBox
            key={itemSideUUID}
            displayMenuOptionUUIDPath={combineUUIDs([
              displayMenuOptionUUIDPath,
              itemSideUUID,
            ])}
            menuContents={menuContents}
            dispatchMenuContents={dispatchMenuContents}
            foodItemOptions={foodItemOptions}
          />
        ))}
      </Stack>
      <PillButton text="Add side group" onClick={handleAdd("option group")} />
    </BorderBox>
  );
};

export default MenuItemMainEditBox;
